// SEO Content Helper - generates intro paragraphs that include H1 keywords

/**
 * Generate an intro paragraph that naturally incorporates H1 keywords
 * @param {string} h1 - The H1 heading text
 * @param {string} pageType - Type of page (home, country, type, variety, region, appellation, product)
 * @param {object} context - Additional context (dealCount, country, wineType, etc.)
 */
export const generateSEOIntro = (h1, pageType, context = {}) => {
  const { dealCount = 0, country, wineType, variety, region, appellation, price, rating } = context;
  
  // Extract key words from H1 (remove common words)
  const stopWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'best', 'top', 'great', 'deals', 'under', 'wine', 'wines'];
  const h1Words = h1.toLowerCase().split(/\s+/).filter(word => !stopWords.includes(word) && word.length > 2);
  
  switch (pageType) {
    case 'home':
      return `Welcome to Great Wine Deals, your destination for ${h1.toLowerCase()}. ` +
        `We've curated ${dealCount > 0 ? dealCount : 'hundreds of'} exceptional wines, each selected for outstanding value and quality. ` +
        `Whether you're a seasoned collector or just discovering wine, find your perfect bottle today.`;
    
    case 'country':
      return `Explore our handpicked selection of ${h1.toLowerCase()}. ` +
        `${country || 'This region'} produces some of the world's most celebrated wines, and we've found ${dealCount > 0 ? dealCount : 'the best'} outstanding values under $30. ` +
        `From bold reds to crisp whites, discover why ${country || 'these'} wines are beloved worldwide.`;
    
    case 'type':
      return `Browse our collection of ${h1.toLowerCase()}. ` +
        `We've selected ${dealCount > 0 ? dealCount : 'top-rated'} ${wineType || 'wines'} that offer exceptional quality without the premium price tag. ` +
        `Each bottle is chosen for its outstanding value, character, and drinkability.`;
    
    case 'variety':
      return `Discover ${h1.toLowerCase()} from around the world. ` +
        `${variety || 'This grape variety'} produces wines ranging from light and fruity to rich and complex. ` +
        `Our ${dealCount > 0 ? dealCount : 'curated'} selections showcase the best expressions of ${variety || 'this variety'} at incredible prices.`;
    
    case 'region':
      return `Explore ${h1.toLowerCase()}. ` +
        `${region || 'This wine region'} is renowned for producing distinctive wines with unique terroir-driven character. ` +
        `Browse our ${dealCount > 0 ? dealCount : 'carefully selected'} deals to experience the best of ${region || 'the region'}.`;
    
    case 'appellation':
      return `Discover wines from ${h1.toLowerCase()}. ` +
        `${appellation || 'This appellation'} represents a specific growing area known for its exceptional wine quality. ` +
        `Our selection features ${dealCount > 0 ? dealCount : 'outstanding'} bottles that showcase the appellation's distinctive characteristics.`;
    
    case 'product':
      const priceText = price ? `priced at just $${Math.round(price)}` : 'at an exceptional value';
      const ratingText = rating ? `, rated ${rating}` : '';
      return `${h1} is a standout selection in our collection${ratingText}. ` +
        `${priceText}, this wine delivers remarkable quality and character. ` +
        `Read on to discover the tasting notes, food pairings, and what makes this bottle special.`;
    
    case 'multi':
      return `Explore our curated selection of ${h1.toLowerCase()}. ` +
        `We've combined the best of both worlds to bring you ${dealCount > 0 ? dealCount : 'exceptional'} wines that match your specific preferences. ` +
        `Each bottle is selected for quality, value, and authentic character.`;
    
    default:
      return `Discover ${h1.toLowerCase()}. ` +
        `Our expert team has curated ${dealCount > 0 ? dealCount : 'the finest'} selections to help you find exceptional wines at unbeatable prices.`;
  }
};

/**
 * Check if page content already contains H1 keywords
 * @param {string} h1 - The H1 heading
 * @param {string} content - The page content
 * @returns {boolean} - True if H1 keywords are present in content
 */
export const h1KeywordsInContent = (h1, content) => {
  if (!h1 || !content) return false;
  
  const stopWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'best', 'top', 'great', 'deals', 'under', 'wine', 'wines', '$30'];
  const h1Words = h1.toLowerCase().split(/\s+/).filter(word => !stopWords.includes(word) && word.length > 2);
  const contentLower = content.toLowerCase();
  
  // Check if at least 50% of H1 keywords appear in content
  const matchCount = h1Words.filter(word => contentLower.includes(word)).length;
  return matchCount >= Math.ceil(h1Words.length * 0.5);
};
